import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import TaskSkeleton from "./TaskSkeleton";
import Select from "react-select";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Add compact table styles
const compactTableStyles = `
    .compact-summary-table {
        font-size: 0.8rem;
    }
    
    .compact-summary-table thead th {
        font-size: 0.75rem;
        padding: 8px 6px;
        white-space: nowrap;
    }
    
    .compact-summary-table tbody td {
        font-size: 0.75rem;
        padding: 6px 5px;
        white-space: nowrap;
    }
    
    .compact-summary-table tbody td small {
        font-size: 0.7rem;
    }
    
    .compact-summary-table .btn-sm {
        font-size: 0.7rem;
        padding: 3px 6px;
    }
    
    .compact-summary-table a {
        font-size: 0.75rem;
    }

    .date-separator td {
        background-color: #e9ecef !important;
        font-weight: bold;
        font-size: 0.8rem !important;
    }
`;

export default function DailyReportSummary() {
    const { http, user } = AuthUser();
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFrom, setselectedFrom] = useState(moment().format("YYYY-MM-DD"));
    const [selectedTo, setselectedTo] = useState(moment().format("YYYY-MM-DD"));
    const [stClass, setStClass] = useState([]);
    const [selectedClassStud, setselectedClassStud] = useState("");
    const [excludeClass, setExcludeClass] = useState(false);
    const [feeTypes, setFeeTypes] = useState([]);
    const [selectedFeeType, setSelectedFeeType] = useState("");
    const [selectedStdType, setSelectedStdType] = useState("");

    useEffect(() => {
        http.get(`/class?company_id=${user.company_id}`).then((res) => {
            setStClass(res.data.data);
        });

        http.get(`/tution_feeslist?company_id=${user.company_id}`).then((res) => {
            setFeeTypes(res.data.data);
        });

        fetchSummary(selectedFrom, selectedTo, "", false, "", "");
    }, []);

    const fetchSummary = (fromDate, toDate, selClass = "", excClass = false, feeType = "", stdType = "") => {
        setIsLoading(true);
        let endpoint = `/daily_report_summary?date_from=${fromDate}&date_to=${toDate}&company_id=${user.company_id}&class=${excClass ? '' : selClass}&exclude_class=${excClass ? 1 : 0}&fees_type=${feeType}&std_type=${stdType}`;

        http
            .get(endpoint)
            .then((res) => {
                setIsLoading(false);
                if (Array.isArray(res.data.data)) {
                    setTasks(res.data.data);
                } else {
                    setIsLoading(false);
                    console.error("API response is not an array");
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching summary:", error);
            });
    };

    function handleChangeDate(event) {
        setselectedFrom(event.target.value);
    }

    function handleChangeDateEnd(event) {
        setselectedTo(event.target.value);
    }

    const handleFilter = () => {
        fetchSummary(selectedFrom, selectedTo, selectedClassStud, excludeClass, selectedFeeType, selectedStdType);
    };

    const handleToggleExcludeClass = () => {
        const newVal = !excludeClass;
        setExcludeClass(newVal);
        if (newVal) {
            setselectedClassStud("");
        }
        fetchSummary(selectedFrom, selectedTo, "", newVal, selectedFeeType, selectedStdType);
    };

    const handleClearFields = () => {
        window.location.reload();
    };

    const convertToExcel = () => {
        const excelData = [];
        let filterInfo = `Date: ${moment(selectedFrom).format('DD-MM-YYYY')} to ${moment(selectedTo).format('DD-MM-YYYY')}`;
        if (selectedClassStud) filterInfo += ` | Class: ${selectedClassStud}`;
        if (selectedFeeType) {
            const ft = feeTypes.find(f => String(f.id) === String(selectedFeeType));
            if (ft) filterInfo += ` | Fee Type: ${ft.name}`;
        }
        if (excludeClass) filterInfo += ' | Class Excluded';
        if (selectedStdType) filterInfo += ` | Student Type: ${selectedStdType}`;
        excelData.push({ 'Date': filterInfo });
        excelData.push({});
        Object.keys(groupedByDate).forEach((date) => {
            const dateTasks = groupedByDate[date];
            dateTasks.forEach((task) => {
                const row = {
                    'Date': moment(task.paid_date).format("DD MMM, YYYY"),
                    'Fees Type': task.fees_name,
                };
                if (!excludeClass) row['Class'] = task.class;
                row['Type'] = task.std_type || 'N/A';
                row['Concession'] = parseFloat(task.concession_total).toFixed(2);
                row['Cash'] = parseFloat(task.cash_total).toFixed(2);
                row['UPI'] = parseFloat(task.upi_total).toFixed(2);
                row['Dbank'] = parseFloat(task.dbank_total).toFixed(2);
                row['Agent'] = parseFloat(task.mode_total).toFixed(2);
                row['Total'] = parseFloat(task.total).toFixed(2);
                excelData.push(row);
            });
            const dateConcession = dateTasks.reduce((s, t) => s + parseFloat(t.concession_total || 0), 0);
            const dateCash = dateTasks.reduce((s, t) => s + parseFloat(t.cash_total || 0), 0);
            const dateUpi = dateTasks.reduce((s, t) => s + parseFloat(t.upi_total || 0), 0);
            const dateDbank = dateTasks.reduce((s, t) => s + parseFloat(t.dbank_total || 0), 0);
            const dateMode = dateTasks.reduce((s, t) => s + parseFloat(t.mode_total || 0), 0);
            const dateTotal = dateTasks.reduce((s, t) => s + parseFloat(t.total || 0), 0);
            const subRow = {
                'Date': `Sub Total (${moment(date).format("DD MMM, YY")})`,
                'Fees Type': '',
            };
            if (!excludeClass) subRow['Class'] = '';
            subRow['Type'] = '';
            subRow['Concession'] = dateConcession.toFixed(2);
            subRow['Cash'] = dateCash.toFixed(2);
            subRow['UPI'] = dateUpi.toFixed(2);
            subRow['Dbank'] = dateDbank.toFixed(2);
            subRow['Agent'] = dateMode.toFixed(2);
            subRow['Total'] = dateTotal.toFixed(2);
            excelData.push(subRow);
        });
        const grandRow = {
            'Date': 'Grand Total',
            'Fees Type': '',
        };
        if (!excludeClass) grandRow['Class'] = '';
        grandRow['Type'] = '';
        grandRow['Concession'] = concessionSum.toFixed(2);
        grandRow['Cash'] = cashSum.toFixed(2);
        grandRow['UPI'] = upiSum.toFixed(2);
        grandRow['Dbank'] = dbankSum.toFixed(2);
        grandRow['Agent'] = modeSum.toFixed(2);
        grandRow['Total'] = totalSum.toFixed(2);
        excelData.push(grandRow);

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Daily_Report_Summary");
        XLSX.writeFile(wb, `daily_report_summary_${moment().format('YYYY-MM-DD')}.xlsx`);
        toast.success('Excel exported successfully!');
    };

    const convertToPDF = () => {
        const doc = new jsPDF('l', 'mm', 'a4');

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Daily Report Summary', doc.internal.pageSize.getWidth() / 2, 12, { align: 'center' });

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        let filterText = `Date: ${moment(selectedFrom).format('DD-MM-YYYY')} to ${moment(selectedTo).format('DD-MM-YYYY')}`;
        if (selectedClassStud) filterText += ` | Class: ${selectedClassStud}`;
        if (selectedFeeType) {
            const ft = feeTypes.find(f => String(f.id) === String(selectedFeeType));
            if (ft) filterText += ` | Fee Type: ${ft.name}`;
        }
        if (excludeClass) filterText += ' | Class Excluded';
        if (selectedStdType) filterText += ` | Student Type: ${selectedStdType}`;
        doc.text(filterText, 14, 18);

        doc.setFontSize(8);
        doc.text(`Generated on: ${moment().format('DD-MM-YYYY HH:mm')}`, doc.internal.pageSize.getWidth() - 14, 18, { align: 'right' });

        const headers = ['No', 'Date', 'Fees Type'];
        if (!excludeClass) headers.push('Class');
        headers.push('Type', 'Concession', 'Cash', 'UPI', 'Dbank', 'Agent', 'Total');

        const rows = [];
        let counter = 1;
        Object.keys(groupedByDate).forEach((date) => {
            const dateTasks = groupedByDate[date];
            dateTasks.forEach((task) => {
                const row = [
                    counter,
                    moment(task.paid_date).format("DD MMM, YY"),
                    task.fees_name,
                ];
                if (!excludeClass) row.push(task.class);
                row.push(task.std_type || 'N/A');
                row.push(
                    parseFloat(task.concession_total).toFixed(2),
                    parseFloat(task.cash_total).toFixed(2),
                    parseFloat(task.upi_total).toFixed(2),
                    parseFloat(task.dbank_total).toFixed(2),
                    parseFloat(task.mode_total).toFixed(2),
                    parseFloat(task.total).toFixed(2)
                );
                rows.push(row);
                counter++;
            });
            const dateConcession = dateTasks.reduce((s, t) => s + parseFloat(t.concession_total || 0), 0);
            const dateCash = dateTasks.reduce((s, t) => s + parseFloat(t.cash_total || 0), 0);
            const dateUpi = dateTasks.reduce((s, t) => s + parseFloat(t.upi_total || 0), 0);
            const dateDbank = dateTasks.reduce((s, t) => s + parseFloat(t.dbank_total || 0), 0);
            const dateMode = dateTasks.reduce((s, t) => s + parseFloat(t.mode_total || 0), 0);
            const dateTotal = dateTasks.reduce((s, t) => s + parseFloat(t.total || 0), 0);
            const subRow = ['', `Sub Total (${moment(date).format("DD MMM, YY")})`, ''];
            if (!excludeClass) subRow.push('');
            subRow.push('');
            subRow.push(dateConcession.toFixed(2), dateCash.toFixed(2), dateUpi.toFixed(2), dateDbank.toFixed(2), dateMode.toFixed(2), dateTotal.toFixed(2));
            rows.push(subRow);
        });
        const grandRow = ['', 'Grand Total', ''];
        if (!excludeClass) grandRow.push('');
        grandRow.push('');
        grandRow.push(concessionSum.toFixed(2), cashSum.toFixed(2), upiSum.toFixed(2), dbankSum.toFixed(2), modeSum.toFixed(2), totalSum.toFixed(2));
        rows.push(grandRow);

        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: 22,
            theme: 'grid',
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [52, 152, 219], textColor: 255, fontStyle: 'bold', halign: 'center' },
            didParseCell: function (data) {
                const numColStart = excludeClass ? 4 : 5;
                if (data.column.index >= numColStart) {
                    data.cell.styles.halign = 'right';
                }
                if (data.section === 'body') {
                    const rowData = data.row.raw;
                    if (rowData[0] === '' && (String(rowData[1]).includes('Sub Total') || String(rowData[1]).includes('Grand Total'))) {
                        data.cell.styles.fontStyle = 'bold';
                        if (String(rowData[1]).includes('Grand Total')) {
                            data.cell.styles.fillColor = [220, 230, 241];
                        } else {
                            data.cell.styles.fillColor = [240, 240, 240];
                        }
                    }
                }
            },
            didDrawPage: function (data) {
                doc.setFontSize(8);
                doc.setTextColor(128);
                const pageCount = doc.internal.getNumberOfPages();
                const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
                doc.text(
                    `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
                    doc.internal.pageSize.getWidth() / 2,
                    pageHeight - 10,
                    { align: 'center' }
                );
            }
        });

        doc.save(`daily_report_summary_${moment().format('YYYY-MM-DD')}.pdf`);
        toast.success('PDF exported successfully!');
    };

    // Compute totals
    const concessionSum = tasks.reduce((total, t) => total + parseFloat(t.concession_total || 0), 0);
    const cashSum = tasks.reduce((total, t) => total + parseFloat(t.cash_total || 0), 0);
    const upiSum = tasks.reduce((total, t) => total + parseFloat(t.upi_total || 0), 0);
    const dbankSum = tasks.reduce((total, t) => total + parseFloat(t.dbank_total || 0), 0);
    const modeSum = tasks.reduce((total, t) => total + parseFloat(t.mode_total || 0), 0);
    const totalSum = tasks.reduce((total, t) => total + parseFloat(t.total || 0), 0);

    // Group tasks by date for sub-totals
    const groupedByDate = {};
    tasks.forEach((task) => {
        const date = task.paid_date;
        if (!groupedByDate[date]) {
            groupedByDate[date] = [];
        }
        groupedByDate[date].push(task);
    });

    // Build rows with date sub-totals
    const renderRows = () => {
        const rows = [];
        let counter = 1;

        Object.keys(groupedByDate).forEach((date) => {
            const dateTasks = groupedByDate[date];

            // Data rows for this date
            dateTasks.forEach((task) => {
                rows.push(
                    <tr key={`row-${counter}`}>
                        <td>{counter}</td>
                        <td>{moment(task.paid_date).format("DD MMM, YY")}</td>
                        <td>{task.fees_name}</td>
                        {!excludeClass && <td>{task.class}</td>}
                        <td><span className={`badge ${task.std_type === 'New' ? 'bg-success' : task.std_type === 'Old' ? 'bg-secondary' : 'bg-light text-dark'}`} style={{ fontSize: '0.68rem' }}>{task.std_type || 'N/A'}</span></td>
                        <td><span style={{ color: 'red' }}><b>{parseFloat(task.concession_total).toFixed(2)}</b></span></td>
                        <td>{parseFloat(task.cash_total).toFixed(2)}</td>
                        <td>{parseFloat(task.upi_total).toFixed(2)}</td>
                        <td>{parseFloat(task.dbank_total).toFixed(2)}</td>
                        <td>{parseFloat(task.mode_total).toFixed(2)}</td>
                        <td><b>{parseFloat(task.total).toFixed(2)}</b></td>
                    </tr>
                );
                counter++;
            });

            // Sub-total row for this date
            const dateConcession = dateTasks.reduce((s, t) => s + parseFloat(t.concession_total || 0), 0);
            const dateCash = dateTasks.reduce((s, t) => s + parseFloat(t.cash_total || 0), 0);
            const dateUpi = dateTasks.reduce((s, t) => s + parseFloat(t.upi_total || 0), 0);
            const dateDbank = dateTasks.reduce((s, t) => s + parseFloat(t.dbank_total || 0), 0);
            const dateMode = dateTasks.reduce((s, t) => s + parseFloat(t.mode_total || 0), 0);
            const dateTotal = dateTasks.reduce((s, t) => s + parseFloat(t.total || 0), 0);

            rows.push(
                <tr key={`subtotal-${date}`} className="date-separator">
                    <td></td>
                    <td colSpan={excludeClass ? "3" : "4"} style={{ textAlign: 'right' }}>
                        Sub Total ({moment(date).format("DD MMM, YY")})
                    </td>
                    <td><span style={{ color: 'red' }}>{dateConcession.toFixed(2)}</span></td>
                    <td>{dateCash.toFixed(2)}</td>
                    <td>{dateUpi.toFixed(2)}</td>
                    <td>{dateDbank.toFixed(2)}</td>
                    <td>{dateMode.toFixed(2)}</td>
                    <td><b>{dateTotal.toFixed(2)}</b></td>
                </tr>
            );
        });

        return rows;
    };

    return (
        <>
            <style>{compactTableStyles}</style>
            <Toaster />
            <div className="container-fluid mt-2">
                <div className="row align-items-end">
                    <div className="col-md-12 mb-2">
                        <h3 className="mb-1">Daily Report Summary</h3>
                        <small className="text-muted">
                            Grouped by Date, Fees Type {!excludeClass && <>&amp; Class</>}
                            {selectedStdType && <span className="badge bg-warning text-dark ms-2">Student Type: {selectedStdType}</span>}
                        </small>
                    </div>
                    <div className="col-md-2 form-group">
                        <input
                            type="date"
                            name="due_date_start"
                            className="form-control border border-success border-2"
                            value={selectedFrom}
                            onChange={handleChangeDate}
                            title="Paid Date From"
                        />
                    </div>

                    <div className="col-md-2 form-group">
                        <input
                            type="date"
                            name="due_date_end"
                            className="form-control border border-success border-2"
                            value={selectedTo}
                            onChange={handleChangeDateEnd}
                            title="Paid Date To"
                        />
                    </div>

                    {!excludeClass && (
                        <div className="col-md-2 form-group">
                            <Select
                                options={[
                                    { value: "", label: "Select Class" },
                                    ...stClass.map((option) => ({
                                        value: option.class_name,
                                        label: option.class_name
                                    }))
                                ]}
                                onChange={(selectedOption) => {
                                    setselectedClassStud(selectedOption ? selectedOption.value : "");
                                }}
                                value={stClass.find(option => option.class_name === selectedClassStud) ?
                                    { value: selectedClassStud, label: selectedClassStud } :
                                    { value: "", label: "Select Class" }}
                                isClearable
                                placeholder="Select Class"
                                menuPlacement="auto"
                                menuPosition="absolute"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '38px',
                                        fontSize: '14px'
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999
                                    })
                                }}
                            />
                        </div>
                    )}

                    <div className="col-md-2 form-group">
                        <Select
                            options={[
                                { value: "", label: "All Fees Type" },
                                ...feeTypes.map((ft) => ({
                                    value: ft.id,
                                    label: ft.name
                                }))
                            ]}
                            onChange={(selectedOption) => {
                                setSelectedFeeType(selectedOption ? selectedOption.value : "");
                            }}
                            value={feeTypes.find(ft => ft.id === selectedFeeType) ?
                                { value: selectedFeeType, label: feeTypes.find(ft => ft.id === selectedFeeType).name } :
                                { value: "", label: "All Fees Type" }}
                            isClearable
                            placeholder="Fees Type"
                            menuPlacement="auto"
                            menuPosition="absolute"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: '38px',
                                    fontSize: '14px'
                                }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 9999
                                })
                            }}
                        />
                    </div>

                    <div className="col-md-1 form-group">
                        <select
                            className="form-control"
                            value={selectedStdType}
                            onChange={(e) => setSelectedStdType(e.target.value)}
                            title="Student Type"
                        >
                            <option value="">All Type</option>
                            <option value="New">New</option>
                            <option value="Old">Old</option>
                        </select>
                    </div>

                    <div className="col-md-1 form-group">
                        <button className="btn btn-primary" onClick={handleFilter}>
                            Search
                        </button>
                    </div>

                    <div className="col-auto form-group">
                        <button
                            className={`btn ${excludeClass ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={handleToggleExcludeClass}
                            title={excludeClass ? "Class excluded - click to include" : "Click to exclude class grouping"}
                        >
                            {excludeClass ? 'Class Excluded' : 'Exclude Class'}
                        </button>
                    </div>

                    <div className="col-auto form-group d-flex gap-2 mt-2">
                        <button className="btn btn-outline-secondary" onClick={handleClearFields}>
                            Clear
                        </button>
                        <button className="btn btn-success" onClick={convertToExcel} disabled={tasks.length === 0}>
                            <i className="fas fa-file-excel me-1"></i>Excel
                        </button>
                        <button className="btn btn-danger" onClick={convertToPDF} disabled={tasks.length === 0}>
                            <i className="fas fa-file-pdf me-1"></i>PDF
                        </button>
                    </div>
                </div>

                <hr />
                <div className="row">
                    {isLoading ? (
                        <TaskSkeleton />
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover mt-2 compact-summary-table">
                                <thead className="table-primary">
                                    <tr>
                                        <th style={{ minWidth: '40px' }}>No</th>
                                        <th style={{ minWidth: '90px' }}>Date</th>
                                        <th style={{ minWidth: '120px' }}>Fees Type</th>
                                        {!excludeClass && <th style={{ minWidth: '70px' }}>Class</th>}
                                        <th style={{ minWidth: '50px' }}>Type</th>
                                        <th style={{ minWidth: '80px' }}>Concession</th>
                                        <th style={{ minWidth: '70px' }}>Cash</th>
                                        <th style={{ minWidth: '70px' }}>UPI</th>
                                        <th style={{ minWidth: '70px' }}>Dbank</th>
                                        <th style={{ minWidth: '70px' }}>Mode</th>
                                        <th style={{ minWidth: '80px' }}>Total</th>
                                    </tr>
                                    <tr>
                                        <th></th>
                                        <th></th>
                                        <th></th>
                                        {!excludeClass && <th></th>}
                                        <th></th>
                                        <th><span style={{ color: 'red' }}>(<b>{concessionSum.toFixed(2)}</b>)</span></th>
                                        <th>(<b>{cashSum.toFixed(2)}</b>)</th>
                                        <th>(<b>{upiSum.toFixed(2)}</b>)</th>
                                        <th>(<b>{dbankSum.toFixed(2)}</b>)</th>
                                        <th>(<b>{modeSum.toFixed(2)}</b>)</th>
                                        <th>(<b>{totalSum.toFixed(2)}</b>)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderRows()}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
